import { Button, CircularProgress, Box, styled, Typography } from '@mui/material';
import { keyframes } from '@mui/system';
import type { ButtonProps } from '@mui/material';

const zoomIn = keyframes`
  0% {
    transform: scale(1);
  }
  100% {
    transform: scale(1.02);
  }
`;

interface PyrenzButtonProps extends ButtonProps {
  dataState?: 'loading' | string;
}

export const PyrenzBlueButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'dataState',
})<PyrenzButtonProps>(({ theme, dataState }) => ({
  color: theme.palette.mode === 'dark' ? '#fff' : '#1a1a2e',
  backgroundColor: theme.palette.mode === 'dark'
    ? 'rgba(255, 255, 255, 0.05)'
    : 'rgba(0, 0, 0, 0.03)',
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
  borderRadius: 12,
  padding: '10px 24px',
  fontSize: '0.95rem',
  fontWeight: 500,
  letterSpacing: '0.3px',
  textTransform: 'none',
  transition: 'all 250ms cubic-bezier(0.4, 0, 0.2, 1)',
  border: `1px solid ${
    theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.1)'
      : 'rgba(0, 0, 0, 0.08)'
  }`,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
  position: 'relative',
  overflow: 'hidden',

  '&:hover': {
    animation: `${zoomIn} 0.25s ease forwards`,
    backgroundColor: theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.08)'
      : 'rgba(0, 0, 0, 0.06)',
    border: `1px solid ${
      theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.2)'
        : 'rgba(0, 0, 0, 0.12)'
    }`,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
  },

  '&:active': {
    transform: 'scale(0.98)',
    backgroundColor: theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.03)'
      : 'rgba(0, 0, 0, 0.08)',
  },

  ...(dataState === 'loading' && {
    opacity: 0.7,
    pointerEvents: 'none',
  }),
}));

export const PyrenzBlueButtonWithLoading = ({
  dataState,
  sx,
  children,
  ...props
}: PyrenzButtonProps) => {
  return (
    <Box position="relative" display="inline-block">
      <PyrenzBlueButton dataState={dataState} sx={sx} {...props}>
        <Box
          sx={{
            opacity: dataState === 'loading' ? 0 : 1,
            transition: 'opacity 0.2s',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          {children}
        </Box>
      </PyrenzBlueButton>
      {dataState === 'loading' && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <CircularProgress size={18} sx={{ color: 'inherit' }} />
          <Typography
            variant="caption"
            sx={{
              color: 'inherit',
              fontWeight: 500,
              fontSize: '0.8rem',
            }}
          >
            Loading
          </Typography>
        </Box>
      )}
    </Box>
  );
};